import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createFileRoute } from "@tanstack/react-router";
import { Camera, CloudOff, Crop, Download, History, Send } from "lucide-react";
import QRCode from "qrcode";
import { type ReactNode, useEffect, useState } from "react";

/** `/download/scanner`: Netlion Scanner, the Android app for photographing documents. */

export const Route = createFileRoute("/_app/download_/scanner")({
  component: ScannerDownloadPage,
});

const FEATURES: Array<{ icon: ReactNode; title: string; body: string }> = [
  {
    icon: <Camera className="h-5 w-5" />,
    title: "Photograph",
    body: "One big button opens the phone's camera. Shoot the whole receipt or invoice.",
  },
  {
    icon: <Crop className="h-5 w-5" />,
    title: "Cleaned up on its own",
    body: "The app finds the sheet, crops and straightens it, turns it upright and evens out the light. Rotate by hand or send the original if it guessed wrong.",
  },
  {
    icon: <Send className="h-5 w-5" />,
    title: "Send",
    body: "The document lands in accounting's inbox, where its details are read and an accountant confirms them.",
  },
  {
    icon: <CloudOff className="h-5 w-5" />,
    title: "Works without a signal",
    body: "Photos wait on the phone and leave by themselves once it is back online.",
  },
  {
    icon: <History className="h-5 w-5" />,
    title: "Recently sent",
    body: "The list of your last documents and how far each one got: being read, waiting for the accountant, in the books.",
  },
];

const APK_PATH = "/app/netlion-scanner.apk";

function ScannerDownloadPage() {
  const apkUrl = `${window.location.origin}${APK_PATH}`;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(apkUrl, { margin: 1, width: 360 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [apkUrl]);

  return (
    <div className="mx-auto max-w-4xl space-y-10 py-6" data-testid="scanner-download-page">
      <section className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <img src="/apps/scanner.svg" alt="" className="h-14 w-14" />
            <div>
              <h1 className="text-2xl font-semibold">Netlion Scanner</h1>
              <p className="text-muted-foreground">
                The Android app for receipts and invoices: photograph, send, done. It does nothing
                else.
              </p>
            </div>
          </div>
          <Button asChild>
            <a href={APK_PATH}>
              <Download className="mr-2 h-4 w-4" />
              Download for Android
            </a>
          </Button>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Open this page on the phone, or scan the code.</li>
            <li>Download the app and open the file; Android asks to allow installing it once.</li>
            <li>Sign in with GitLab, the same as in Hub.</li>
          </ol>
        </div>
        {qr && (
          <img
            src={qr}
            alt="QR code with the download link"
            className="h-44 w-44 self-center rounded-lg border bg-white p-2 md:self-start"
          />
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        {FEATURES.map((f) => (
          <Card key={f.title}>
            <CardContent className="flex gap-3 p-4">
              <div className="mt-0.5 shrink-0 text-primary">{f.icon}</div>
              <div className="space-y-1">
                <p className="font-medium">{f.title}</p>
                <p className="text-sm text-muted-foreground">{f.body}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
}
